import {
  FIELD_FLAG,
  FIELD_FLAG_REST_LAYERS,
  FIELD_FLAG_TEXTURE_KEYS,
  FIELD_FLAG_TEXTURE_TRIMS,
} from '../configs/fieldFlags';
import {MELEE_TEXTURE_KEYS} from '../configs/meleeAnimation';
import {resolveFlagClothLight, resolveFlagClothOffset, shadeFlagColor} from '../utils/effects/fieldFlagPoses';
import {resolveMarkerSeed, resolvePulseShare, resolveRisingPose} from '../utils/effects/liveMarkerPoses';
import {ensureMeleeTextures} from '../utils/effects/meleeEffectTextures';
import {resolveActiveGlowPose} from './ActiveUnitGlows';

const {cloth, frameSize, scale, segments, shading, finial: finialConfig, sparks: sparksConfig} = FIELD_FLAG;

// Слои внутри жетона: все выше подложки квартала, по порядку
const LAYER_STEP = 0.002;
const GRID_STEP = frameSize / (segments - 1);
const FRAME_HALF = frameSize / 2;

const getTint = (color, alpha) => (((((alpha * 255) | 0) & 0xff) << 24) | color) >>> 0;

/**
 * Обойти столбцы слоя полотнища: границы — узлы общей сетки волны, крайние обрезаны по
 * картинке слоя. Смещение между узлами — по прямой. Чистый расчет: так он и проверяется.
 *
 * @param {number} left — левый край картинки в точках кадра
 * @param {number} width — ширина картинки
 * @param {number[]} offsets — смещение в каждом узле сетки
 * @param {function(number, number, number, number, number, number): void} visit —
 *   (левый x, правый x, смещение слева, смещение справа, узел слева, узел справа)
 */
export const visitFlagColumns = (left, width, offsets, visit) => {
  const right = left + width;
  let xA = left;
  let indexA = Math.min(segments - 2, Math.floor(left / GRID_STEP));
  let offsetA = offsets[indexA] + (offsets[indexA + 1] - offsets[indexA]) * (left / GRID_STEP - indexA);
  while (xA < right) {
    const indexB = indexA + 1;
    const gridX = indexB * GRID_STEP;
    const isClipped = gridX >= right;
    const xB = isClipped ? right : gridX;
    const offsetB = isClipped
      ? offsets[indexA] + (offsets[indexB] - offsets[indexA]) * (right / GRID_STEP - indexA)
      : offsets[indexB];
    visit(xA, xB, offsetA, offsetB, indexA, indexB);
    xA = xB;
    offsetA = offsetB;
    indexA = indexB;
    if (indexA >= segments - 1) {
      break;
    }
  }
};

/**
 * Отрисовка слоя живого полотнища: вместо прямоугольника картинка идет столбцами со
 * смещением по волне. Столбцы уходят в общий пакет наравне со спрайтами. Phaser Rope на
 * это не годится: каждая полоса сбрасывает пакет, и пять полос на флаг при семи флагах
 * давали 35 лишних вызовов отрисовки на кадр, что на телефоне заметно.
 *
 * Сигнатура и порядок вызовов те же, что у batchSprite. Вызывает Phaser, this — не нужен
 */
export const renderFlagClothLayer = (renderer, src, camera) => {
  const {flag} = src;
  const halfWorld = FRAME_HALF * scale;
  const view = camera.worldView;
  // Флаг за краем экрана не рисуем: он не успевает зайти в кадр между двумя кадрами
  if (src.x + halfWorld < view.x || src.x - halfWorld > view.right
    || src.y + halfWorld < view.y || src.y - halfWorld > view.bottom) {
    return;
  }

  camera.addToRenderList(src);
  const pipeline = renderer.pipelines.set(src.pipeline, src);
  let unit = pipeline.setGameObject(src);
  const {frame} = src;
  const {a, b, c, d, e, f} = camera.matrix;
  const originX = src.x - camera.scrollX * src.scrollFactorX;
  const originY = src.y - camera.scrollY * src.scrollFactorY;
  const alpha = camera.alpha * src.alpha;
  const baseTint = getTint(src.tintTopLeft, alpha);
  const colors = src.isClothColored ? flag.clothColors : null;
  const topY = (frame.y - FRAME_HALF) * scale;
  const bottomY = topY + frame.height * scale;
  const frameLeft = frame.x;
  const {width: frameWidth, u0: frameU0, u1: frameU1, v0, v1, glTexture} = frame;
  const tintEffect = src.tintFill;

  pipeline.manager.preBatch(src);
  visitFlagColumns(frameLeft, frameWidth, flag.offsets, (xA, xB, offsetA, offsetB, indexA, indexB) => {
    const worldA = originX + (xA - FRAME_HALF) * scale;
    const worldB = originX + (xB - FRAME_HALF) * scale;
    const topA = originY + topY + offsetA;
    const bottomA = originY + bottomY + offsetA;
    const topB = originY + topY + offsetB;
    const bottomB = originY + bottomY + offsetB;
    const u0 = frameU0 + (frameU1 - frameU0) * ((xA - frameLeft) / frameWidth);
    const u1 = frameU0 + (frameU1 - frameU0) * ((xB - frameLeft) / frameWidth);
    const tintA = colors ? getTint(colors[indexA], alpha) : baseTint;
    const tintB = colors ? getTint(colors[indexB], alpha) : baseTint;
    if (pipeline.shouldFlush(6)) {
      pipeline.flush();
      unit = pipeline.setGameObject(src);
    }
    pipeline.batchQuad(
      src,
      worldA * a + topA * c + e, worldA * b + topA * d + f,
      worldA * a + bottomA * c + e, worldA * b + bottomA * d + f,
      worldB * a + bottomB * c + e, worldB * b + bottomB * d + f,
      worldB * a + topB * c + e, worldB * b + topB * d + f,
      u0, v0, u1, v1,
      tintA, tintB, tintA, tintB,
      tintEffect, glTexture, unit,
    );
  });
  pipeline.manager.postBatch(src);
};

/**
 * ФЛАГИ ЦЕНТРАЛЬНЫХ КВАРТАЛОВ (см. configs/fieldFlags). Части флага — обычные
 * объекты поля: их регистрирует и убирает ячейка, а прячет полет двойника жетона.
 *
 * Ткань живого флага — картинки со своей отрисовкой (renderFlagClothLayer) и общей
 * сеткой волны: тень, свечение, контур, ткань и ее блики. Без волны столбцы складываются
 * в картинку неподвижного флага. Движение идет от часов сцены: флаг пересоздается на
 * обновление стора и машет дальше.
 */
export default class FieldFlags {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [options]
   * @param {number} [options.linearFilter] — режим фильтрации LINEAR для текстур искр
   */
  constructor(scene, {linearFilter} = {}) {
    this.scene = scene;
    this.linearFilter = linearFilter;
    this.flags = new Set();
    this.isListening = false;
    this.areFramesReady = false;
    this.tick = this.tick.bind(this);
  }

  now() {
    return this.scene?.time?.now ?? 0;
  }

  /**
   * Картинки слоев обрезаны по содержимому, а спрайт все равно ведет себя как кадр во
   * весь жетон: это обычная обрезка кадра в Phaser. Прозрачное поле GPU не закрашивает
   */
  ensureFrames() {
    if (this.areFramesReady || !this.scene?.textures?.get) {
      return;
    }
    Object.entries(FIELD_FLAG_TEXTURE_TRIMS).forEach(([key, {x, y, width, height}]) => {
      if (this.scene.textures.exists?.(key)) {
        this.scene.textures.get(key)?.get?.()?.setTrim(frameSize, frameSize, x, y, width, height);
      }
    });
    this.areFramesReady = true;
  }

  /** Ключи картинок неподвижного флага снизу вверх и какие из них красить в цвет игрока */
  getRestLayers() {
    this.ensureFrames();
    return FIELD_FLAG_REST_LAYERS;
  }

  /**
   * @param {Object} params
   * @param {number} params.x — центр жетона
   * @param {number} params.y
   * @param {number} params.hexSize
   * @param {number} params.depth — слой жетона
   * @param {number} params.color — цвет игрока, 0xRRGGBB
   * @param {boolean} [params.isWaving] — флаг машет
   * @param {boolean} [params.hasGlow] — вокруг него яркий контур, свечение и искры
   * @returns {Object[]} части флага снизу вверх
   */
  createFlag({x, y, hexSize, depth, color, isWaving = false, hasGlow = false}) {
    this.ensureFrames();
    if (!isWaving || typeof this.scene?.add?.image !== 'function') {
      return this.createRestFlag({x, y, depth, color});
    }
    ensureMeleeTextures(this.scene, {linearFilter: this.linearFilter});
    return this.createWavingFlag({x, y, hexSize, depth, color, hasGlow});
  }

  createRestFlag({x, y, depth, color}) {
    return this.getRestLayers().map(({textureKey, isTinted}, index) => {
      const sprite = this.scene.add.sprite(x, y, textureKey);
      sprite.setScale(scale);
      sprite.setDepth(depth + LAYER_STEP * (index + 1));
      if (isTinted) {
        sprite.setTint(color);
      }
      return sprite;
    });
  }

  createWavingFlag({x, y, hexSize, depth, color, hasGlow}) {
    const parts = [];
    let layer = 1;
    const place = (part) => {
      part.setDepth(depth + LAYER_STEP * layer);
      layer += 1;
      parts.push(part);
      return part;
    };
    const addFrame = (textureKey) => {
      const sprite = place(this.scene.add.sprite(x, y, textureKey));
      sprite.setScale(scale);
      return sprite;
    };

    const flag = {
      x,
      y,
      hexSize,
      seed: resolveMarkerSeed(x, y),
      color,
      offsets: new Array(segments).fill(0),
      clothColors: new Array(segments).fill(color),
    };
    const addClothLayer = (textureKey, {isClothColored = false} = {}) => {
      const image = addFrame(textureKey);
      image.flag = flag;
      image.isClothColored = isClothColored;
      image.renderWebGL = renderFlagClothLayer;
      return image;
    };

    // Тени снизу: свечение и контур не должны ими глушиться
    addClothLayer(FIELD_FLAG_TEXTURE_KEYS.SHADOW);
    addFrame(FIELD_FLAG_TEXTURE_KEYS.POLE_SHADOW);
    const glowStrip = hasGlow ? addClothLayer(FIELD_FLAG_TEXTURE_KEYS.GLOW) : null;
    const poleGlow = hasGlow ? addFrame(FIELD_FLAG_TEXTURE_KEYS.POLE_GLOW) : null;
    const contourStrip = hasGlow ? addClothLayer(FIELD_FLAG_TEXTURE_KEYS.CONTOUR) : null;
    const poleContour = hasGlow ? addFrame(FIELD_FLAG_TEXTURE_KEYS.POLE_CONTOUR) : null;
    const clothStrip = addClothLayer(FIELD_FLAG_TEXTURE_KEYS.CLOTH, {isClothColored: true});
    const lightStrip = addClothLayer(FIELD_FLAG_TEXTURE_KEYS.CLOTH_LIGHT);
    addFrame(FIELD_FLAG_TEXTURE_KEYS.POLE);

    const addGlowImage = (partX, partY, textureKey, sizePx, tint) => {
      const image = place(this.scene.add.image(partX, partY, textureKey));
      image.baseScale = sizePx / (image.width || 1);
      image.setScale(image.baseScale);
      image.setTint(tint);
      return image;
    };
    flag.finial = hasGlow
      ? addGlowImage(x + finialConfig.x, y + finialConfig.y, MELEE_TEXTURE_KEYS.GLOW, hexSize * finialConfig.sizeFactor, finialConfig.tint)
      : null;
    flag.sparks = hasGlow
      ? Array.from({length: sparksConfig.count}, (_, index) => {
        const spark = addGlowImage(x, y, MELEE_TEXTURE_KEYS.BURST, hexSize * sparksConfig.sizeFactor, sparksConfig.tints[index % sparksConfig.tints.length]);
        spark.setAlpha(0);
        return spark;
      })
      : [];
    Object.assign(flag, {clothStrip, lightStrip, glowStrip, poleGlow, contourStrip, poleContour});

    this.flags.add(flag);
    this.updateFlag(flag, this.now());
    if (!this.isListening && this.scene?.events?.on) {
      this.scene.events.on('update', this.tick);
      this.isListening = true;
    }
    return parts;
  }

  updateFlag(flag, now) {
    const {offsets, seed, color, clothStrip, lightStrip, clothColors, glowStrip, poleGlow, contourStrip, poleContour, finial, sparks, x, y, hexSize} = flag;
    for (let index = 0; index < segments; index += 1) {
      // Доля длины полотнища: левее места крепления точки стоят на месте
      const clothT = (index * GRID_STEP - cloth.leftPx) / cloth.lengthPx;
      offsets[index] = resolveFlagClothOffset(clothT, now, seed);
      clothColors[index] = shadeFlagColor(color, resolveFlagClothLight(clothT, now, seed), shading.light, shading.dark);
    }

    // Блики, контур и свечение гаснут вместе с тканью: ее прячет полет двойника и мигание
    const master = clothStrip.alpha ?? 1;
    lightStrip.setAlpha(master);
    if (glowStrip) {
      const pose = resolveActiveGlowPose(now);
      glowStrip.setAlpha(master * pose.alpha);
      poleGlow.setAlpha(master * pose.alpha);
      contourStrip.setAlpha(master);
      poleContour.setAlpha(master);
    }
    if (finial) {
      const share = resolvePulseShare(now, finialConfig.pulseMs, seed);
      finial.setAlpha(master * (finialConfig.minAlpha + (finialConfig.maxAlpha - finialConfig.minAlpha) * share));
    }
    for (let index = 0; index < sparks.length; index += 1) {
      const spark = sparks[index];
      const {share, alpha, sway} = resolveRisingPose(now, seed, index, sparks.length, sparksConfig.periodMs);
      spark.setPosition(
        x + sparksConfig.x + hexSize * sparksConfig.spreadFactor * sway,
        y + sparksConfig.y - hexSize * sparksConfig.riseFactor * share,
      );
      spark.setAlpha(master * alpha);
    }
  }

  tick() {
    const now = this.now();
    this.flags.forEach((flag) => {
      if (flag.clothStrip.active === false) {
        this.flags.delete(flag);
        return;
      }
      this.updateFlag(flag, now);
    });
    if (this.flags.size === 0 && this.isListening) {
      this.scene?.events?.off?.('update', this.tick);
      this.isListening = false;
    }
  }

  /** Забыть все флаги (разбор поля). Части убирают их ячейки */
  clear() {
    this.flags.clear();
    if (this.isListening) {
      this.scene?.events?.off?.('update', this.tick);
      this.isListening = false;
    }
  }
}
